import type { SxProps, Theme } from "@mui/material/styles";
import {
  DEFAULT_TRANSITION,
  NEUTRAL_LIGHT,
  PRIMARY_COLOR,
} from "../../theme/consts";
import { generalSectionSx } from "../Layout";

// Scroll container - determines scroll length for parallax animations
// Uses svh (small viewport height) for stable sizing on mobile
// Negative margin creates overlap with previous section (Hero + Connector) for smooth transition
export const scrollContainerSx: SxProps<Theme> = {
  height: "200svh",
  position: "relative",
  marginTop: { xs: "-2rem", md: "-10svh", "2xl": "-20svh" },
  zIndex: 1,
};

// Sticky container - stays in viewport during scroll
// Uses svh for stable sizing that doesn't change with mobile address bar
// No background - allows "push" effect where previous section remains visible
export const stickyContainerSx: SxProps<Theme> = {
  position: "sticky",
  top: 0,
  height: "100svh",
  width: "100%",
  display: "flex",
  // alignItems: "center",
  // justifyContent: "center",
  overflow: "hidden",
  ...generalSectionSx,
  paddingBottom: { "2xl": "6rem" },
};

// Section container with background
export const sectionContainerSx: SxProps<Theme> = {
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  // alignItems: "center",
  // justifyContent: "center",
};

// Background arrow SVG - parallax animated
export const backgroundArrowSx: SxProps<Theme> = {
  position: "absolute",
  left: "50%",
  top: "50%",
  width: "100%",
  maxWidth: "none",
  zIndex: 0,
  pointerEvents: "none",
  opacity: 0.8,
  willChange: "transform", // Optimize for animation
  // Transform controlled by GSAP
};

// Content wrapper
export const contentWrapperSx: SxProps<Theme> = {
  position: "relative",
  zIndex: 1,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
};

// Title section
export const titleSectionSx: SxProps<Theme> = {
  flexDirection: { xs: "column", md: "row", "2xl": "column" },
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "2rem",
};

export const descriptionSx: SxProps<Theme> = {
  color: "text.secondary",
  // fontSize: "1rem",
  maxWidth: { xs: "500px", "2xl": "100%" },
  flex: "1 1 auto",
};

// Cards wrapper - contains carousel and indicators
export const cardsWrapperSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  // Mobile: take remaining space after title, Desktop: auto
  flex: 1,
  minHeight: { xs: 0, md: "auto" },
  gap: "2rem",
  width: "100%",
};

// Cards container - Desktop only (hidden on mobile)
export const cardsContainerSx: SxProps<Theme> = {
  display: { xs: "none", md: "flex" },
  gap: 2,
  width: "100%",
  height: { xs: "400px", "2xl": "500px" },
};

// Embla viewport - Mobile only (hidden on desktop)
export const emblaViewportSx: SxProps<Theme> = {
  display: { xs: "flex", md: "none" },
  overflow: "hidden",
  width: "100%",
  maxHeight: "400px",
  // Take remaining space after indicators
  flex: 1,
  minHeight: 0,
};

// Embla container
export const emblaContainerSx: SxProps<Theme> = {
  display: "flex",
  gap: "1rem",
  width: "100%",
  height: "100%",
  alignItems: "stretch",
};

// Base card styles
export const baseCardSx: SxProps<Theme> = {
  position: "relative",
  borderRadius: "2rem",
  padding: { xs: " 2.5rem 1rem", sm: "2.5rem 1.5rem", md: "2.5rem 2rem" },
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  overflow: "hidden",
  transition: DEFAULT_TRANSITION,
  gap: "2rem",
  // Mobile: full width, fluid height to fit in 100vh
  // Desktop: 100% height to fill container
  flexShrink: 0,
  height: "100%",
  minHeight: { xs: "200px", md: "auto" },
  minWidth: { xs: "100%", md: "auto" },
  // Allow content scroll if it overflows
  overflowY: "auto",
};

// Active card styles
export const activeCardSx: SxProps<Theme> = {
  ...baseCardSx,
  flex: 2,
  background: "linear-gradient(132.28deg, #2B2742 26.19%, #6D63A8 92.3%)",
  color: "#FFFFFF",
  "& .MuiTypography-root": {
    color: "#FFFFFF",
  },
};

// Inactive card styles
export const inactiveCardSx: SxProps<Theme> = {
  ...baseCardSx,
  flex: 1,
  background: "#D9D9D94D",
  backdropFilter: "blur(18px)",
  border: "1px solid #FFFFFF",
  color: "text.primary",
  "& .MuiTypography-root": {
    color: "text.primary",
  },
};

// Card title
export const cardTitleSx: SxProps<Theme> = {
  fontSize: { xs: "24px", sm: "30px", md: "24px", lg: "30px" },
  lineHeight: 1.25,
  fontWeight: 500,
  zIndex: 2,
};

// Card description
export const cardDescriptionSx: SxProps<Theme> = {
  fontSize: "22px",
  lineHeight: 1.6,
  zIndex: 2,
  position: "relative",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: { xs: "auto", md: 3, lg: 2 },
  WebkitBoxOrient: "vertical",
};

// Card mark SVG - fade in only, instant disappear
export const getCardMarkSx = (isActive: boolean): SxProps<Theme> => ({
  opacity: isActive ? 1 : 0,
  transition: isActive ? "all .5s ease-out" : "none",
  zIndex: 2,
  width: { xs: "60px", sm: "80px", md: "120px" },
});

// Indicators container - only visible on mobile
export const indicatorsContainerSx: SxProps<Theme> = {
  display: { xs: "flex", md: "none" },
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
};

// Indicator dot styles
export const getIndicatorSx = (isActive: boolean): SxProps<Theme> => ({
  width: isActive ? "1rem" : "0.75rem",
  height: isActive ? "1rem" : "0.75rem",
  borderRadius: "50%",
  backgroundColor: isActive ? PRIMARY_COLOR : NEUTRAL_LIGHT,
  transition: DEFAULT_TRANSITION,
});
